import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from "@/shared/api";

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user';
  status: 'pending' | 'approved' | 'rejected';
  created_at?: string | null;
}

interface Props {
  currentUserId: string;
}

const STATUS_LABEL: Record<AdminUser['status'], string> = {
  pending: 'Ожидает',
  approved: 'Активен',
  rejected: 'Отклонён',
};

const dateFmt = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

export function AdminPage({ currentUserId }: Props) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiFetch('/api/admin/users');
      if (resp.ok) setUsers((await resp.json()) as AdminUser[]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const action = async (
    id: string,
    path: string,
    method: 'POST' | 'DELETE' = 'POST',
    body?: unknown,
  ) => {
    setBusyId(id);
    setMenuOpenId(null);
    try {
      await apiFetch(`/api/admin/users/${id}${path}`, {
        method,
        ...(body !== undefined
          ? {
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            }
          : {}),
      });
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const pendingCount = users.filter(u => u.status === 'pending').length;

  // Pending first, then by created_at desc (as returned by backend)
  const sorted = [...users].sort((a, b) => {
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (b.status === 'pending' && a.status !== 'pending') return 1;
    return 0;
  });

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <div className="admin-title">Пользователи</div>
          <div className="admin-subtitle">Одобрение заявок, управление доступом</div>
        </div>
      </div>

      {pendingCount > 0 && (
        <div className="admin-pending-banner">
          {pendingCount} {pluralize(pendingCount, ['заявка', 'заявки', 'заявок'])} ожидает рассмотрения
        </div>
      )}

      {loading ? (
        <div className="admin-empty">Загрузка…</div>
      ) : users.length === 0 ? (
        <div className="admin-empty">Нет пользователей</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <colgroup>
              <col />
              <col className="admin-col-narrow" />
              <col className="admin-col-narrow" />
              <col className="admin-col-date" />
              <col className="admin-col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th>Пользователь</th>
                <th>Роль</th>
                <th>Статус</th>
                <th>Зарегистрирован</th>
                <th aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {sorted.map(u => {
                const initial = (u.name || u.email || '?').trim()[0]?.toUpperCase() || '?';
                const isSelf = u.id === currentUserId;
                const busy = busyId === u.id;
                return (
                  <tr
                    key={u.id}
                    className={u.status === 'pending' ? 'admin-row--pending' : undefined}
                  >
                    <td>
                      <div className="admin-user-cell">
                        <div className="admin-user-avatar">{initial}</div>
                        <div className="admin-user-info">
                          <div className="admin-user-name">
                            {u.name || '—'}
                            {isSelf && <span className="admin-badge admin-badge--self">вы</span>}
                          </div>
                          <div className="admin-user-email">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {u.role === 'admin' ? (
                        <span className="admin-badge admin-badge--admin">Админ</span>
                      ) : (
                        <span className="admin-cell-muted">Пользователь</span>
                      )}
                    </td>
                    <td>
                      <span className={`admin-badge admin-badge--${u.status}`}>
                        {STATUS_LABEL[u.status]}
                      </span>
                    </td>
                    <td className="admin-cell-muted">
                      {u.created_at ? dateFmt.format(new Date(u.created_at)) : '—'}
                    </td>
                    <td>
                      <div className="admin-row-actions">
                        {u.status === 'pending' && (
                          <>
                            <button
                              className="admin-btn admin-btn--primary"
                              disabled={busy}
                              onClick={() => action(u.id, '/approve')}
                            >
                              Одобрить
                            </button>
                            <button
                              className="admin-btn"
                              disabled={busy}
                              onClick={() => action(u.id, '/reject')}
                            >
                              Отклонить
                            </button>
                          </>
                        )}
                        {!isSelf && hasOverflowMenu(u) && (
                          <div className="admin-menu-wrap">
                            <button
                              type="button"
                              className="admin-kebab"
                              aria-label="Действия"
                              disabled={busy}
                              onClick={() =>
                                setMenuOpenId(menuOpenId === u.id ? null : u.id)
                              }
                            >
                              <KebabIcon />
                            </button>
                            {menuOpenId === u.id && (
                              <>
                                <div
                                  className="admin-menu-backdrop"
                                  onClick={() => setMenuOpenId(null)}
                                />
                                <div className="admin-menu" role="menu">
                                  {u.status === 'rejected' && (
                                    <button
                                      type="button"
                                      className="admin-menu-item"
                                      onClick={() => action(u.id, '/approve')}
                                    >
                                      Восстановить
                                    </button>
                                  )}
                                  {u.status === 'approved' && u.role !== 'admin' && (
                                    <button
                                      type="button"
                                      className="admin-menu-item"
                                      onClick={() =>
                                        action(u.id, '/role', 'POST', { role: 'admin' })
                                      }
                                    >
                                      Сделать админом
                                    </button>
                                  )}
                                  {u.role === 'admin' && (
                                    <button
                                      type="button"
                                      className="admin-menu-item"
                                      onClick={() => {
                                        if (confirm(`Снять админа с ${u.email}?`))
                                          action(u.id, '/role', 'POST', { role: 'user' });
                                      }}
                                    >
                                      Снять админа
                                    </button>
                                  )}
                                  {u.status === 'approved' && u.role !== 'admin' && (
                                    <button
                                      type="button"
                                      className="admin-menu-item"
                                      onClick={() => action(u.id, '/reject')}
                                    >
                                      Заблокировать
                                    </button>
                                  )}
                                  {u.role !== 'admin' && (
                                    <button
                                      type="button"
                                      className="admin-menu-item admin-menu-item--danger"
                                      onClick={() => {
                                        if (confirm(`Удалить пользователя ${u.email}?`))
                                          action(u.id, '', 'DELETE');
                                      }}
                                    >
                                      Удалить
                                    </button>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// A row gets a kebab menu when there is at least one overflow action
// available besides the inline approve/reject pair on pending rows.
function hasOverflowMenu(u: AdminUser): boolean {
  if (u.status === 'rejected') return true;
  if (u.status === 'approved' && u.role !== 'admin') return true;
  if (u.role === 'admin') return true;
  return false;
}

function KebabIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  );
}

function pluralize(n: number, forms: [string, string, string]): string {
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 19) return forms[2];
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}
